import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
    FiArrowUpRight,
    FiTrendingUp,
    FiPieChart,
    FiShield,
    FiDollarSign,
    FiActivity,
    FiDownload
} from 'react-icons/fi';
import financialService from '../../services/financialService';
import { toast } from 'react-hot-toast';

const IncomeDashboard = () => {
    const [incomeData, setIncomeData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadIncome = async () => {
            try {
                const data = await financialService.getIncomeBreakdown();
                setIncomeData(data);
            } catch (err) {
                console.error('Failed to load income data:', err);
                toast.error('Could not fetch income breakdown.');
            } finally {
                setLoading(false);
            }
        };
        loadIncome();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-premium-wood-dark flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-premium-gold"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-premium-wood-dark p-8 ml-64 text-white">
            <header className="mb-12 flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-serif font-bold text-premium-gold mb-2">Income Growth</h1>
                    <p className="text-white/60">Real-time wealth tracking across the platform realms.</p>
                </div>
                <button className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-lg hover:bg-white/10 transition text-sm">
                    <FiDownload className="text-premium-gold" />
                    Export Report
                </button>
            </header>

            {/* Top Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                <StatCard
                    icon={<FiDollarSign />}
                    label="Total Income"
                    value={`${incomeData?.total.toLocaleString()} ${incomeData?.currency}`}
                    trend="+12.5%"
                />
                <StatCard
                    icon={<FiTrendingUp />}
                    label="Growth Velocity"
                    value="Strong"
                    trend="High Priority"
                    color="text-green-400"
                />
                <StatCard
                    icon={<FiPieChart />}
                    label="Income Diversity"
                    value="Excellent"
                    trend="3 Realms Active"
                    color="text-blue-400"
                />
                <StatCard
                    icon={<FiShield />}
                    label="Trust Stability"
                    value="98.2%"
                    trend="Elite Tier"
                    color="text-premium-gold"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Growth Chart Area */}
                <div className="lg:col-span-2 bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 relative overflow-hidden">
                    <h3 className="text-xl font-serif text-white/90 mb-8 flex items-center gap-2">
                        <FiActivity className="text-premium-gold" />
                        Income Performance
                    </h3>

                    {/* Simulated Chart visualization */}
                    <div className="h-64 flex items-end justify-between gap-4 mt-12 px-4">
                        {incomeData?.growth.map((point: any, i: number) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-4 group">
                                <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: `${(point.amount / 3000) * 100}%` }}
                                    className="w-full bg-gradient-to-t from-premium-gold/20 to-premium-gold rounded-t-lg relative group-hover:brightness-125 transition-all"
                                >
                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-white text-black px-2 py-1 rounded text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                                        ${point.amount}
                                    </div>
                                </motion.div>
                                <span className="text-xs text-white/40">{point.month}</span>
                            </div>
                        ))}
                    </div>

                    <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                        <FiTrendingUp size={200} />
                    </div>
                </div>

                {/* Realm Breakdown */}
                <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8">
                    <h3 className="text-xl font-serif text-white/90 mb-8">Income by Realm</h3>
                    <div className="space-y-6">
                        {incomeData?.byRealm.map((realm: any, i: number) => (
                            <div key={i} className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-white/60">{realm.realm}</span>
                                    <span className="text-premium-gold font-bold">{realm.percentage}%</span>
                                </div>
                                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${realm.percentage}%` }}
                                        className="h-full bg-premium-gold"
                                    />
                                </div>
                                <p className="text-[10px] text-white/30 uppercase tracking-widest">
                                    {realm.amount.toLocaleString()} {incomeData.currency}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 p-4 bg-premium-gold/5 border border-premium-gold/20 rounded-xl">
                        <p className="text-xs text-premium-gold leading-relaxed">
                            💡 <strong>Elite Tip:</strong> Increase your "Local Gig" activity to 30% to unlock the 'Localized Wealth' badge and lower your transaction fees.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ icon, label, value, trend, color = "text-white" }: any) => (
    <motion.div
        whileHover={{ y: -5 }}
        className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6 shadow-xl"
    >
        <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white/5 rounded-lg text-premium-gold">
                {icon}
            </div>
            <span className="text-xs uppercase text-white/40 tracking-widest">{label}</span>
        </div>
        <div className={`text-2xl font-bold mb-2 ${color}`}>
            {value}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-green-400 font-bold">
            <FiArrowUpRight />
            {trend}
        </div>
    </motion.div>
);

export default IncomeDashboard;
